/**
 * CommunitySavedResourcesPage — §10 Saved Resources.
 * The volunteer's bookmarked library resources (getSavedResources), with
 * Read/Watch/Share/Remove actions. Read/Watch open the resource's URL; Share
 * records a social share via shareResource; Remove unbooksaves it. No financial
 * concepts.
 */
import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Bookmark, ExternalLink, Share2, Trash2, FileText, Video } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovEmptyState } from '@/components/community/CommunityLayout'
import { SectionLoading } from '@/components/community/ui'
import {
  getSavedResources, unsaveResource, shareResource, type SavedResourceItem,
} from '@/services/resourceService'

export default function CommunitySavedResourcesPage() {
  const { t } = useTranslation()
  const [items, setItems] = useState<SavedResourceItem[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    getSavedResources(0, 60)
      .then((p) => setItems(p.content))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const onRemove = async (r: SavedResourceItem) => {
    setBusyId(r.id)
    try { await unsaveResource(r.id); setItems((p) => p.filter((x) => x.id !== r.id)) } catch { /* ignore */ }
    finally { setBusyId(null) }
  }
  const onShare = async (r: SavedResourceItem) => {
    setBusyId(r.id)
    try { await shareResource(r.id) } catch { /* ignore */ } finally { setBusyId(null) }
  }

  const href = (r: SavedResourceItem) => r.externalUrl || r.fileUrl || '#'
  const isVideo = (r: SavedResourceItem) => r.resourceType === 'VIDEO'

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.savedResources.title')} subtitle={t('community.savedResources.subtitle')} icon={Bookmark} />

      {loading ? <SectionLoading rows={3} /> : items.length === 0 ? (
        <GovEmptyState icon="🔖" title={t('community.savedResources.none')} description={t('community.savedResources.noneHint')} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((r) => (
            <GovCard key={r.id}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  {isVideo(r)
                    ? <Video className="w-5 h-5 text-blue-600" aria-hidden="true" />
                    : <FileText className="w-5 h-5 text-blue-600" aria-hidden="true" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 line-clamp-2">{r.title}</h3>
                  {r.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{r.description}</p>}
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-400 flex-wrap">
                    <span>{t(`community.resources.type_${r.resourceType}`, r.resourceType)}</span>
                    {r.sourceOrg && <span>· {r.sourceOrg}</span>}
                    {r.language && <span>· {r.language}</span>}
                  </div>
                  <div className="mt-3 flex items-center gap-4 flex-wrap">
                    <a href={href(r)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline min-h-[40px]">
                      <ExternalLink className="w-4 h-4" aria-hidden="true" />
                      {isVideo(r) ? t('community.savedResources.watch') : t('community.savedResources.read')}
                    </a>
                    <button onClick={() => onShare(r)} disabled={busyId === r.id}
                      className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:underline disabled:opacity-50 min-h-[40px]">
                      <Share2 className="w-4 h-4" aria-hidden="true" />{t('community.actions.share')}
                    </button>
                    <button onClick={() => onRemove(r)} disabled={busyId === r.id}
                      className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:underline disabled:opacity-50 min-h-[40px]">
                      <Trash2 className="w-4 h-4" aria-hidden="true" />{t('community.savedResources.remove')}
                    </button>
                  </div>
                </div>
              </div>
            </GovCard>
          ))}
        </div>
      )}
    </CommunityLayout>
  )
}